import {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ISetFilterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import _ from 'lodash'
import { ShieldAlert } from 'lucide-react'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'

import { controlStatusLabel } from '@/const/label'

import { calculatePercentage } from '@/lib/collection/math-utils'
import { CONTROL_STATUS } from '@/lib/connect/proto-types'

import { ControlScopeCell } from '@/pages/control/ag-grid-control/components/cells/control-scope-cell'
import { ControlStatusCell } from '@/pages/control/ag-grid-control/components/cells/control-status-cell'
import { ControlTitleCell } from '@/pages/control/ag-grid-control/components/cells/control-title-cell'
import { ControlTitleGroupCell } from '@/pages/control/ag-grid-control/components/cells/control-title-group-cell'
import { getCommentsColumnDef } from '@/pages/control/ag-grid-control/config/comments-column-def'
import type { ControlRow } from '@/pages/control/ag-grid-control/types'

import { Progress } from '@/components/lemmala-design-system/progress/progress'
import { TABLE_COLUMN_WIDTHS } from '@/components/lemmala-design-system/table/consts'
import { Sentiment } from '@/components/lemmala-design-system/variance-percentage/types'

const WIDTH_CONTROL = 200
const WIDTH_THIRD_PARTY_ADHERENCE = 382
const WIDTH_VALIDATION_STATUS = 180
const WIDTH_SCOPE = TABLE_COLUMN_WIDTHS.lg

type GetAssessmentControlColumnDefsProps = {
  controls: ControlRow[]
  readOnly: boolean
  hasManagePermissions: boolean
  groupByScope: boolean
}

export const getAssessmentControlColumnDefs = ({
  controls,
  readOnly = false,
  hasManagePermissions = false,
  groupByScope = false,
}: GetAssessmentControlColumnDefsProps): ColDef<ControlRow>[] => {
  const cols: ColDef<ControlRow>[] = [
    {
      colId: 'control',
      // No value while grouped: AG Grid skips refreshing a cell whose value did not change, and a
      // scope header keeps the same value across expand/collapse, which left its chevron stale.
      field: groupByScope ? undefined : 'title',
      headerName: 'Control',
      minWidth: WIDTH_CONTROL,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // When grouped: render scope header
        if (groupByScope && params.data?.isGroupHeader) {
          return <ControlTitleGroupCell {...params} />
        }

        // Regular control cell - show category + name
        return <ControlTitleCell {...params} />
      },
      colSpan: (params: { data?: ControlRow }) => {
        // Only span when NOT grouped
        if (!groupByScope && params.data?.isGroupHeader) {
          return 4 // Span across all columns when not grouped (control, scope, status, comments)
        }
        return 1
      },
      cellClass: 'py-3',
      getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
        const { title, description, categoryName } = params.data || {}
        return [title, description, categoryName].join(' ')
      },
      valueGetter: groupByScope
        ? undefined
        : (params: ValueGetterParams<ControlRow>) => params.data?.title || '',
      sortable: !groupByScope,
      cellDataType: false,
    },
    // Scope column (scope.name) - always visible in control tab. When grouped, header rows show scope name too.
    {
      hide: groupByScope,
      colId: 'scope',
      field: 'scope',
      headerName: 'Scope',
      width: WIDTH_SCOPE,
      sortable: true,
      cellRenderer: ControlScopeCell,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: _.uniq(controls.map((control) => control.scope?.name ?? '')),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ControlRow>) => {
        if (params.data?.isGroupHeader) return params.data.categoryName ?? ''
        return params.data?.scope?.name ?? ''
      },
      cellDataType: false,
    },
    // Progress bar column (only when groupByScope is true)
    {
      colId: 'adherence',
      headerName: 'Control adherence',
      hide: !groupByScope,
      minWidth: WIDTH_THIRD_PARTY_ADHERENCE,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // Only render for group headers
        if (params.data?.isGroupHeader) {
          const { categoryMetrics } = params.data
          if (!categoryMetrics) return null

          const { totalValidated, total } = categoryMetrics

          return (
            <div className='flex size-full items-center gap-6 pr-2'>
              <Progress
                value={calculatePercentage({ amount: totalValidated, total })}
                className='h-1.75 w-full'
                sentiment={Sentiment.POSITIVE}
              />
              <span className='text-nowrap text-body-default'>
                {totalValidated} / {total}
              </span>
            </div>
          )
        }
        // Empty for regular control rows
        return null
      },
      cellDataType: false,
    },
    // Hidden column for status filtering (always named "Validation status" in filter bar)
    {
      colId: 'status',
      field: 'status',
      headerName: 'Validation status',
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: CONTROL_STATUS.map((status) => controlStatusLabel[status.number as ControlStatus]),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ControlRow>) => {
        if (params.data?.isGroupHeader) {
          return params.data.categoryFilterValues?.statuses || []
        }
        const status = params.data?.status
        return status === undefined ? '' : String(status)
      },
      filterValueGetter: (params: ValueGetterParams<ControlRow>) => {
        if (params.data?.isGroupHeader) {
          return params.data.categoryFilterValues?.statuses || []
        }
        const status = params.data?.status
        return status !== undefined ? controlStatusLabel[status] || '' : ''
      },
    },
    // Visible validation status / Gaps column
    {
      colId: 'statusDisplay',
      field: 'status',
      headerName: groupByScope ? 'Gaps' : 'Validation status',
      minWidth: WIDTH_VALIDATION_STATUS,
      cellClass: 'py-3',
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // When grouped, show gaps indicator for group headers
        if (groupByScope && params.data?.isGroupHeader) {
          const { categoryMetrics } = params.data
          if (!categoryMetrics) return null

          const { gapsCount } = categoryMetrics

          return (
            <div className='flex size-full items-center text-body-default'>
              {gapsCount > 0 ? (
                <div className='flex items-center gap-1 text-main-foreground-primary'>
                  {gapsCount} <ShieldAlert className={`size-4 text-main-foreground-danger`} />
                </div>
              ) : (
                <span className='text-main-foreground-tertiary'>–</span>
              )}
            </div>
          )
        }

        // Regular status cell for control rows
        return (
          <ControlStatusCell
            {...params}
            readOnly={readOnly}
            hasManagePermissions={hasManagePermissions}
            hideLastCalculatedDate={readOnly}
            from='assessment-controls-tab'
          />
        )
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
        if (params.data?.isGroupHeader) {
          return ''
        }
        const status = params.data?.status
        if (status === undefined) return ''
        return controlStatusLabel[status] || ''
      },
      sortable: !groupByScope,
      cellDataType: false,
    },
  ]

  // Headerless comments column (icon + count) shared with the Issues tab
  cols.push(getCommentsColumnDef({ sortable: !groupByScope }))

  return cols
}
